import { compressImageFile } from "@bto/shared";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { faCircleCheck, faSpinner } from "@fortawesome/pro-solid-svg-icons";
import { IFileWithMeta } from "react-dropzone-uploader";

import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { Button } from "../shared/Button/Button";

import { FileDropzoneUploader } from "./FileDropzoneUploader/FileDropzoneUploader";
import {
  ProfileModalButtons,
  ProfileModalTitle,
  ProfileModalTitleWrapper,
} from "../shared/Modal/Modal.styles";
import {
  CenteredModal,
  FAModalInstructionText,
  ModalCloseButton,
  UpfrontModalContentWrapper,
  UpfrontPaymentFinancialModalWrapper,
  UpfrontPaymentFinancialReceipt,
  UpfrontPaymentFinancialReceiptTitle,
  UpfrontPaymentSecondaryReceiptTitle,
} from "./UpfrontPayment.styles";
import {
  Bill,
  SubmitFASupportingDocument,
} from "./Services/UpfrontPaymentService";
import { toBase64 } from "../../utils/helpers";
import { BillingSupportNumber } from "./types";
import { themePalette } from "../../theme";

const MAX_FILES = 10;
const AUTO_DISMISS_TIMEOUT_MS = 5000;

interface FASupportingDocumentUploadModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setUpfrontBill: React.Dispatch<React.SetStateAction<Bill | undefined>>;
}

export const FASupportingDocumentUploadModal: React.FC<
  FASupportingDocumentUploadModalProps
> = ({ open, setOpen, setUpfrontBill }) => {
  const { deviceType } = useDeviceHook();
  const { t } = useTranslation();
  const { billingPK, adminID } = useParams<{
    billingPK?: string;
    adminID?: string;
  }>();

  const [files, setFiles] = useState<IFileWithMeta[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (!isSuccess) return undefined;

    const timer = window.setTimeout(() => {
      setOpen(false);
      resetState();
    }, AUTO_DISMISS_TIMEOUT_MS);

    return () => window.clearTimeout(timer);
  }, [isSuccess, setOpen]);

  const resetState = () => {
    setFiles([]);
    setError(null);
    setLoading(false);
    setIsSuccess(false);
  };

  const closeModal = () => {
    setOpen(false);
    resetState();
  };

  const submitDocuments = async () => {
    if (files.length === 0) return;
    if (!billingPK) {
      setError(
        t("upfrontPayment.faDocumentUpload.errors.missingBilling", {
          phone: BillingSupportNumber,
        }),
      );
      return;
    }
    setError(null);
    setLoading(true);
    const totalFiles = files.length;
    const pending = [...files];
    let uploaded = 0;

    const handlePartialFailure = () => {
      if (uploaded > 0) {
        const failedNames = pending.map((f) => f.file.name).join(", ");
        setFiles(pending);
        setError(
          t("upfrontPayment.faDocumentUpload.errors.partialFailure", {
            uploaded,
            total: totalFiles,
            failedNames,
            phone: BillingSupportNumber,
          }),
        );
      } else {
        setError(
          t("upfrontPayment.faDocumentUpload.errors.uploadFailed", {
            phone: BillingSupportNumber,
          }),
        );
      }
    };

    try {
      while (pending.length > 0) {
        const entry = pending[0];
        const compressedFile = await compressImageFile(entry.file);
        const base64Document = await toBase64(compressedFile);
        const response = await SubmitFASupportingDocument(
          base64Document,
          billingPK,
          adminID,
        );
        if (!response.ok) {
          break;
        }
        const bill = (await response.json()) as Bill;
        setUpfrontBill(bill);
        pending.shift();
        uploaded += 1;
      }
      if (pending.length === 0) {
        setIsSuccess(true);
      } else {
        handlePartialFailure();
      }
    } catch {
      handlePartialFailure();
    } finally {
      setLoading(false);
    }
  };

  return (
    <CenteredModal open={open} onClose={closeModal}>
      <>
        {isSuccess ? (
          <UpfrontPaymentFinancialModalWrapper
            deviceType={deviceType}
            allowScroll={false}
            receipt
          >
            <UpfrontPaymentFinancialReceipt deviceType={deviceType}>
              <FontAwesomeIcon
                icon={faCircleCheck}
                color={themePalette.greenSuccess}
                size="2x"
              />
              <UpfrontPaymentFinancialReceiptTitle>
                {t("upfrontPayment.faDocumentUpload.thankYou")}
              </UpfrontPaymentFinancialReceiptTitle>
              <UpfrontPaymentSecondaryReceiptTitle>
                {t("upfrontPayment.faDocumentUpload.followUp")}
              </UpfrontPaymentSecondaryReceiptTitle>
            </UpfrontPaymentFinancialReceipt>
          </UpfrontPaymentFinancialModalWrapper>
        ) : (
          <UpfrontPaymentFinancialModalWrapper
            deviceType={deviceType}
            allowScroll
          >
            <ProfileModalTitle deviceType={deviceType}>
              <ProfileModalTitleWrapper deviceType={deviceType}>
                <div>{t("upfrontPayment.financialAssistance.title")}</div>
                <ModalCloseButton onClick={closeModal}>
                  <FontAwesomeIcon icon={faXmark} />
                </ModalCloseButton>
              </ProfileModalTitleWrapper>
            </ProfileModalTitle>
            <UpfrontModalContentWrapper>
              <FAModalInstructionText>
                {t("upfrontPayment.financialAssistance.documentUploadTitle")}
              </FAModalInstructionText>
              <FileDropzoneUploader
                files={files}
                setFiles={setFiles}
                error={error}
                setError={setError}
                maxFiles={MAX_FILES}
              />
            </UpfrontModalContentWrapper>

            <ProfileModalTitle deviceType={deviceType}>
              <ProfileModalButtons deviceType={deviceType}>
                <Button
                  className="upfront-cancel-button"
                  label={t("upfrontPayment.actions.cancel")}
                  onClick={closeModal}
                  size="medium"
                  reverse
                  textColor="greyMedium"
                  backgroundColor="greyMedium"
                  disabled={loading}
                />
                <Button
                  className="upfront-submit-button"
                  label={loading ? "" : t("upfrontPayment.actions.submit")}
                  onClick={submitDocuments}
                  size="medium"
                  backgroundColor="greenDark"
                  disabled={files.length === 0 || loading}
                  startIcon={
                    loading ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : undefined
                  }
                />
              </ProfileModalButtons>
            </ProfileModalTitle>
          </UpfrontPaymentFinancialModalWrapper>
        )}
      </>
    </CenteredModal>
  );
};
